import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import BundleTagsManager from './BundleTagsManager';
import { 
  User, Mail, Lock, Phone, MapPin, Trophy, Calendar, Briefcase, 
  Camera, Check, ArrowRight, ArrowLeft, Loader2, Eye, EyeOff, Globe
} from 'lucide-react';
import { toast } from 'sonner';

// Auto-detect API URL based on current domain (same as AuthModal)
const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001/api';
  }
  // Use same origin + /api for production
  return window.location.origin + '/api';
};

const API_URL = getApiUrl();

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 
  'Delaware', 'District of Columbia', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 
  'Wisconsin', 'Wyoming'
];

const INDUSTRIES = [
  'Finance', 'Real Estate', 'Technology', 'Healthcare', 'Legal', 'Entrepreneur', 
  'Marketing', 'Sales', 'Education', 'Government', 'Retired', 'Other'
];

// Categories for the Business Network section (mirrors backend BUSINESS_CATEGORIES)
const BUSINESS_CATEGORIES = [
  'Real Estate', 'Finance & Investing', 'Legal', 'Insurance', 'Healthcare & Medical',
  'Construction & Trades', 'Tech & Software', 'Hospitality & Restaurants', 'Automotive',
  'Marketing & Media', 'Consulting', 'Retail & E-commerce', 'Education & Coaching',
  'Fitness & Wellness', 'Golf Industry', 'Other'
];

export function SignupModal({ isOpen, onClose, onSuccess, onLoginClick, prefillCode = '', prefillData = null }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    // Section 1 - Basic Info
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirm_password: '',
    phone: '',
    city: '',
    state: '',
    zip_code: '',
    // Section 2 - Golf Profile
    handicap: '',
    average_score: '',
    years_playing: '',
    home_course: '',
    favorite_courses: '',
    // Section 3 - Playing Preferences
    play_frequency: '',
    preferred_days: [],
    preferred_tee_time: '',
    // Section 4 - Networking
    profession: '',
    golf_goals: '',
    // Business Network (all optional — published on /network if business_name + business_description filled)
    business_category: '',
    business_name: '',
    business_description: '',
    business_website: '',
    // Section 5 - Bundle Community
    interested_in: [],
    // Bundle Tags — hashtag-based discovery (min 3 required at signup)
    tags: [],
    // Section 6 - Profile Photo
    profile_photo: null,
    // Section 7 - Invite Code
    invite_code: '',
    referred_by: '',
    referred_by_user_id: '',
    // Section 8 - Agreement
    agree_terms: false
  });

  // When opened with a verified Request Invitation handoff, pre-fill the invite code
  // and basic info so the user doesn't re-type what they already entered.
  useEffect(() => {
    if (!isOpen) return;
    setFormData(prev => {
      const next = { ...prev };
      if (prefillCode && !next.invite_code) next.invite_code = prefillCode;
      if (prefillData) {
        Object.entries(prefillData).forEach(([k, v]) => {
          if (v != null && v !== '' && (next[k] == null || next[k] === '')) {
            next[k] = v;
          }
        });
      }
      return next;
    });
  }, [isOpen, prefillCode, prefillData]);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Photo must be less than 5MB');
        return;
      }
      setFormData(prev => ({ ...prev, profile_photo: file }));
      const reader = new FileReader();
      reader.onload = (e) => setPhotoPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const validateStep = (stepNum) => {
    switch (stepNum) {
      case 1:
        if (!formData.first_name || !formData.last_name || !formData.email || !formData.password || !formData.confirm_password) {
          toast.error('Please fill in all required fields');
          return false;
        }
        if (formData.password !== formData.confirm_password) {
          toast.error('Passwords do not match');
          return false;
        }
        if (formData.password.length < 6) {
          toast.error('Password must be at least 6 characters');
          return false;
        }
        if (!formData.phone || formData.phone.replace(/\D/g, '').length < 10) {
          toast.error('Please enter a valid phone number');
          return false;
        }
        if (!formData.city || !formData.state || !formData.zip_code) {
          toast.error('Please enter your location');
          return false;
        }
        return true;
      case 3:
        if (!formData.tags || formData.tags.length < 3) {
          toast.error('Please pick at least 3 Bundle Tags so other members can find you');
          return false;
        }
        return true;
      case 4:
        if (!formData.invite_code) {
          toast.error('Please enter your invite code');
          return false;
        }
        if (!formData.agree_terms) {
          toast.error('Please agree to the membership terms');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(prev => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;

    setLoading(true);
    try {
      // First, upload profile photo if provided
      let photoUrl = null;
      if (formData.profile_photo) {
        try {
          const photoFormData = new FormData();
          photoFormData.append('file', formData.profile_photo);
          const photoRes = await fetch(`${API_URL}/upload/media`, {
            method: 'POST',
            credentials: 'include',
            body: photoFormData
          });
          if (photoRes.ok) {
            const photoData = await photoRes.json();
            photoUrl = photoData.media_url;
          }
        } catch (photoError) {
          console.error('Photo upload failed, continuing without photo:', photoError);
          // Continue without photo - don't block registration
        }
      }

      // Register user
      const registerData = {
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        name: `${formData.first_name.trim()} ${formData.last_name.trim()}`,
        picture: photoUrl,
        phone: formData.phone,
        invite_code: formData.invite_code.trim().toUpperCase(),
        referred_by: (formData.referred_by || '').trim() || null,
        referred_by_user_id: (formData.referred_by_user_id || '').trim() || null,
        tags: formData.tags || [],
        profile: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone: formData.phone,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zip_code,
          handicap: formData.handicap ? parseFloat(formData.handicap) : null,
          average_score: formData.average_score ? parseInt(formData.average_score) : null,
          years_playing: formData.years_playing,
          home_course: formData.home_course,
          favorite_courses: formData.favorite_courses,
          play_frequency: formData.play_frequency,
          preferred_days: formData.preferred_days,
          preferred_tee_time: formData.preferred_tee_time,
          profession: formData.profession,
          golf_goals: formData.golf_goals,
          interested_in: formData.interested_in
        }
      };

      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(registerData)
      });

      let data = {};
      try {
        data = await response.json();
      } catch (e) {
        // If we can't parse JSON, use status-based error messages
        console.error('Could not parse response JSON');
      }

      if (response.ok) {
        // If user filled out the optional Business Network section, publish their business profile.
        // Fire-and-forget — never block signup completion on this.
        const hasBusinessProfile = formData.business_name.trim() && formData.business_description.trim().length >= 10;
        if (hasBusinessProfile) {
          try {
            await fetch(`${API_URL}/business/profile`, {
              method: 'PUT',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                profession: (formData.profession || 'Professional').slice(0, 80),
                category: formData.business_category || 'Other',
                company: formData.business_name.trim() || null,
                description: formData.business_description.trim(),
                city: formData.city || null,
                state: formData.state || null,
                website: formData.business_website.trim() || null,
                instagram: null,
                linkedin: null,
                logo_url: null,
                phone: formData.phone || null,
                email_public: null,
              }),
            });
          } catch (err) {
            console.error('Business Network publish failed (non-blocking):', err);
          }
        }
        toast.success('Welcome to The Bundle Golf Club!');
        onSuccess(data);
        onClose();
      } else {
        // Show specific error from backend or status-based error
        let errorMsg = data?.detail || data?.message;
        if (!errorMsg) {
          if (response.status === 403) {
            errorMsg = 'Invalid invite code. Contact a Bundle member to get one.';
          } else if (response.status === 400) {
            errorMsg = 'Registration failed. Please check your information.';
          } else {
            errorMsg = 'Registration failed. Please try again.';
          }
        }
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Unable to connect. Please check your internet and try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-6">
      {[1, 2, 3, 4].map((s) => (
        <div
          key={s}
          className={`w-3 h-3 rounded-full transition-colors ${
            s === step ? 'bg-bundle-green' : s < step ? 'bg-bundle-gold' : 'bg-gray-200'
          }`}
        />
      ))}
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0">
        <DialogTitle className="sr-only">Join Bundle Golf Club</DialogTitle>
        {/* Header */}
        <div className="bg-bundle-green p-6 text-center sticky top-0 z-10">
          <h2 className="font-heading font-bold text-2xl text-white mb-2">
            Join Bundle Golf Club
          </h2>
          <p className="font-body text-white/80 text-sm">
            Founding member access — complimentary through June 2026.
          </p>
        </div>

        <div className="p-6">
          {renderStepIndicator()}

          {/* Step 1: Basic Information */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-bundle-green/10 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-bundle-green" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-bundle-green">Basic Information</h3>
                  <p className="font-body text-sm text-gray-500">Create your account</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-body text-gray-600">First Name *</Label>
                  <Input
                    data-testid="signup-first-name"
                    value={formData.first_name}
                    onChange={(e) => updateField('first_name', e.target.value)}
                    placeholder="John"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-body text-gray-600">Last Name *</Label>
                  <Input
                    data-testid="signup-last-name"
                    value={formData.last_name}
                    onChange={(e) => updateField('last_name', e.target.value)}
                    placeholder="Smith"
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm font-body text-gray-600">Email Address *</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    data-testid="signup-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    placeholder="john@example.com"
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-body text-gray-600">Password *</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      data-testid="signup-password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => updateField('password', e.target.value)}
                      placeholder="••••••••"
                      className="pl-10 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-body text-gray-600">Confirm Password *</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      data-testid="signup-confirm-password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.confirm_password}
                      onChange={(e) => updateField('confirm_password', e.target.value)}
                      placeholder="••••••••"
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-sm font-body text-gray-600">Phone Number *</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    data-testid="signup-phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                    placeholder="(555) 123-4567"
                    className="pl-10"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Required for game notifications</p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-body text-gray-600">City *</Label>
                  <Input
                    data-testid="signup-city"
                    value={formData.city}
                    onChange={(e) => updateField('city', e.target.value)}
                    placeholder="Washington"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-body text-gray-600">State *</Label>
                  <select
                    data-testid="signup-state"
                    value={formData.state}
                    onChange={(e) => updateField('state', e.target.value)}
                    className="mt-1 w-full h-10 px-3 rounded-md border border-gray-200 text-sm"
                  >
                    <option value="">Select</option>
                    {US_STATES.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-sm font-body text-gray-600">Zip Code *</Label>
                  <Input
                    data-testid="signup-zip"
                    value={formData.zip_code}
                    onChange={(e) => updateField('zip_code', e.target.value)}
                    placeholder="20001"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Golf Profile & Playing Preferences */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-bundle-gold/20 rounded-full flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-bundle-gold-dark" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-bundle-green">Golf Profile</h3>
                  <p className="font-body text-sm text-gray-500">Help us connect you with other golfers</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-body text-gray-600">Handicap</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.handicap}
                    onChange={(e) => updateField('handicap', e.target.value)}
                    placeholder="12.5"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-body text-gray-600">Average Score</Label>
                  <Input
                    type="number"
                    value={formData.average_score}
                    onChange={(e) => updateField('average_score', e.target.value)}
                    placeholder="85"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-body text-gray-600">Years Playing</Label>
                  <select
                    value={formData.years_playing}
                    onChange={(e) => updateField('years_playing', e.target.value)}
                    className="mt-1 w-full h-10 px-3 rounded-md border border-gray-200 text-sm"
                  >
                    <option value="">Select</option>
                    <option value="0-1">0-1 years</option>
                    <option value="1-3">1-3 years</option>
                    <option value="3-10">3-10 years</option>
                    <option value="10+">10+ years</option>
                  </select>
                </div>
              </div>

              <div>
                <Label className="text-sm font-body text-gray-600">Home Golf Course</Label>
                <Input
                  value={formData.home_course}
                  onChange={(e) => updateField('home_course', e.target.value)}
                  placeholder="Congressional Country Club"
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="text-sm font-body text-gray-600">Favorite Courses</Label>
                <Input
                  value={formData.favorite_courses}
                  onChange={(e) => updateField('favorite_courses', e.target.value)}
                  placeholder="TPC Potomac, Bethesda CC, Army Navy CC"
                  className="mt-1"
                />
              </div>

              <hr className="my-6" />

              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-bundle-green/10 rounded-full flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-bundle-green" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-bundle-green">Playing Preferences</h3>
                  <p className="font-body text-sm text-gray-500">When do you like to play?</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-body text-gray-600">How often do you play?</Label>
                  <select
                    value={formData.play_frequency}
                    onChange={(e) => updateField('play_frequency', e.target.value)}
                    className="mt-1 w-full h-10 px-3 rounded-md border border-gray-200 text-sm"
                  >
                    <option value="">Select</option>
                    <option value="1-2/month">1-2 times per month</option>
                    <option value="weekly">Once per week</option>
                    <option value="2-3/week">2-3 times per week</option>
                    <option value="daily">Almost every day</option>
                  </select>
                </div>
                <div>
                  <Label className="text-sm font-body text-gray-600">Preferred Tee Time</Label>
                  <select
                    value={formData.preferred_tee_time}
                    onChange={(e) => updateField('preferred_tee_time', e.target.value)}
                    className="mt-1 w-full h-10 px-3 rounded-md border border-gray-200 text-sm"
                  >
                    <option value="">Select</option>
                    <option value="morning">Morning</option>
                    <option value="midday">Midday</option>
                    <option value="afternoon">Afternoon</option>
                    <option value="evening">Evening</option>
                  </select>
                </div>
              </div>

              <div>
                <Label className="text-sm font-body text-gray-600 mb-3 block">Preferred Days to Play</Label>
                <div className="flex flex-wrap gap-2">
                  {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleArrayField('preferred_days', day)}
                      className={`px-3 py-1.5 rounded-full text-sm font-body transition-colors ${
                        formData.preferred_days.includes(day)
                          ? 'bg-bundle-green text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Networking & Interests */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-bundle-green">Networking</h3>
                  <p className="font-body text-sm text-gray-500">Connect with golfers in your field</p>
                </div>
              </div>

              {/* Bundle Tags — required min 3 */}
              <div className="rounded-xl bg-bundle-gold/5 border-2 border-bundle-gold/30 p-4">
                <div className="flex items-start gap-2 mb-3">
                  <div className="w-8 h-8 bg-bundle-gold/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="font-heading font-bold text-bundle-gold-dark">#</span>
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-base text-bundle-green">Bundle Tags <span className="text-bundle-gold-dark text-xs">(required)</span></h4>
                    <p className="font-body text-xs text-gray-600">Pick at least 3 tags to help fellow Bundle members find you — by location, military service, profession, fraternity/sorority, interests, or golf style.</p>
                  </div>
                </div>
                <BundleTagsManager
                  value={formData.tags}
                  onChange={(t) => updateField('tags', t)}
                  maxTags={30}
                  minTags={3}
                />
              </div>

              <hr className="my-6" />

              <div>
                <Label className="text-sm font-body text-gray-600">Profession / Industry</Label>
                <select
                  value={formData.profession}
                  onChange={(e) => updateField('profession', e.target.value)}
                  className="mt-1 w-full h-10 px-3 rounded-md border border-gray-200 text-sm"
                >
                  <option value="">Select your industry</option>
                  {INDUSTRIES.map(industry => (
                    <option key={industry} value={industry}>{industry}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label className="text-sm font-body text-gray-600">Golf Goals</Label>
                <Textarea
                  value={formData.golf_goals}
                  onChange={(e) => updateField('golf_goals', e.target.value)}
                  placeholder="Meet new golfers, play competitive rounds, travel for golf, improve handicap..."
                  className="mt-1"
                  rows={3}
                />
              </div>

              <hr className="my-6" />

              {/* ===== BUSINESS NETWORK (optional) ===== */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-bundle-gold/20 rounded-full flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-bundle-gold-dark" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-bundle-green">Business Network <span className="text-gray-400 text-sm font-normal">(optional)</span></h3>
                  <p className="font-body text-sm text-gray-500">List your business or services on the Bundle Business Network. Other members can find you by industry, city, and state.</p>
                </div>
              </div>

              <div>
                <Label className="text-sm font-body text-gray-600">Industry / Category</Label>
                <select
                  data-testid="signup-business-category"
                  value={formData.business_category}
                  onChange={(e) => updateField('business_category', e.target.value)}
                  className="mt-1 w-full h-10 px-3 rounded-md border border-gray-200 text-sm"
                >
                  <option value="">Select a category</option>
                  {BUSINESS_CATEGORIES.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label className="text-sm font-body text-gray-600">Business Name</Label>
                <Input
                  data-testid="signup-business-name"
                  value={formData.business_name}
                  onChange={(e) => updateField('business_name', e.target.value)}
                  placeholder="Smith & Co. Realty"
                  className="mt-1"
                  maxLength={120}
                />
              </div>

              <div>
                <Label className="text-sm font-body text-gray-600">Short Description</Label>
                <Textarea
                  data-testid="signup-business-description"
                  value={formData.business_description}
                  onChange={(e) => updateField('business_description', e.target.value.slice(0, 250))}
                  placeholder="What you do, how you help fellow golfers (max 250 characters)…"
                  className="mt-1"
                  rows={2}
                  maxLength={250}
                />
                <p className="text-[10px] text-gray-400 mt-0.5 text-right">{formData.business_description.length}/250</p>
              </div>

              <div>
                <Label className="text-sm font-body text-gray-600">Website <span className="text-gray-400 font-normal">(optional)</span></Label>
                <Input
                  data-testid="signup-business-website"
                  value={formData.business_website}
                  onChange={(e) => updateField('business_website', e.target.value)}
                  placeholder="https://yourwebsite.com"
                  className="mt-1"
                  maxLength={200}
                />
                <p className="text-[10px] text-gray-400 mt-0.5">Your city &amp; state from Step 1 will be used for the Business Network listing.</p>
              </div>

              <hr className="my-6" />

              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-bundle-gold/20 rounded-full flex items-center justify-center">
                  <Globe className="w-5 h-5 text-bundle-gold-dark" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-bundle-green">Bundle Community</h3>
                  <p className="font-body text-sm text-gray-500">What are you interested in?</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {[
                  { id: 'travel', label: 'Golf Travel' },
                  { id: 'money_games', label: 'Competitive Money Games' },
                  { id: 'events', label: 'Weekly Club Events' },
                  { id: 'networking', label: 'Networking Rounds' }
                ].map(item => (
                  <label
                    key={item.id}
                    className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                      formData.interested_in.includes(item.id)
                        ? 'border-bundle-green bg-bundle-green/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Checkbox
                      checked={formData.interested_in.includes(item.id)}
                      onCheckedChange={() => toggleArrayField('interested_in', item.id)}
                    />
                    <span className="font-body text-sm text-bundle-green">{item.label}</span>
                  </label>
                ))}
              </div>

              <hr className="my-6" />

              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <Camera className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-bundle-green">Profile Photo</h3>
                  <p className="font-body text-sm text-gray-500">Appears on member directory and leaderboard</p>
                </div>
              </div>

              <div className="flex items-center gap-6">
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="w-24 h-24 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-bundle-green transition-colors overflow-hidden"
                >
                  {photoPreview ? (
                    <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <Camera className="w-8 h-8 text-gray-400" />
                  )}
                </div>
                <div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="mb-2"
                  >
                    {photoPreview ? 'Change Photo' : 'Upload Photo'}
                  </Button>
                  <p className="font-body text-xs text-gray-500">JPG, PNG up to 5MB</p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handlePhotoSelect}
                  className="hidden"
                />
              </div>
            </div>
          )}

          {/* Step 4: Confirmation */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-bundle-green rounded-full flex items-center justify-center">
                  <Check className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-bundle-green">Membership Confirmation</h3>
                  <p className="font-body text-sm text-gray-500">Enter your invite code and review benefits</p>
                </div>
              </div>

              <div className="bg-bundle-cream rounded-xl p-6">
                <h4 className="font-heading font-bold text-bundle-green mb-4">Your membership includes:</h4>
                <ul className="space-y-3">
                  {[
                    'Access to the national golfer network',
                    'Find golfers near you',
                    'Round tracking and leaderboard',
                    'Member chat and golf stories',
                    'Club events and travel opportunities',
                    'Golf equipment buy/sell/trade marketplace'
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 font-body text-bundle-green">
                      <div className="w-5 h-5 bg-bundle-gold/20 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-bundle-green" />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-bundle-green/5 border border-bundle-green/20 rounded-xl p-6 text-center">
                <p className="font-body text-sm text-bundle-green mb-2">Founding Member Offer</p>
                <p className="font-heading text-2xl font-bold text-bundle-green">FREE ACCESS</p>
                <p className="font-body text-gray-600">Complimentary through June 2026</p>
              </div>

              {/* Invite Code Field - positioned near submit for visibility */}
              <div className="bg-bundle-gold/10 border border-bundle-gold rounded-xl p-5">
                <Label className="font-body text-sm font-semibold text-bundle-green block mb-2">
                  Invite Code *
                </Label>
                <Input
                  data-testid="signup-invite-code"
                  type="text"
                  placeholder="Enter your invite code"
                  value={formData.invite_code}
                  onChange={(e) => updateField('invite_code', e.target.value.toUpperCase())}
                  className="text-center font-heading font-bold text-lg tracking-widest uppercase"
                />
                <p className="font-body text-xs text-gray-500 mt-2 text-center">
                  Ask a current member or check the club's promotional materials
                </p>
              </div>

              {/* Who referred you - optional */}
              <div>
                <Label className="font-body text-sm font-semibold text-bundle-green block mb-1">
                  Who referred you? <span className="text-gray-400 font-normal">(optional)</span>
                </Label>
                <Input
                  data-testid="signup-referred-by"
                  type="text"
                  placeholder="Bundle member's name or email"
                  value={formData.referred_by}
                  onChange={(e) => updateField('referred_by', e.target.value)}
                />
              </div>

              <label className="flex items-start gap-3 cursor-pointer" data-testid="signup-terms-label">
                <Checkbox
                  data-testid="signup-agree-terms"
                  checked={formData.agree_terms}
                  onCheckedChange={(checked) => updateField('agree_terms', checked)}
                  className="mt-1"
                />
                <span className="font-body text-sm text-gray-600">
                  I agree to the membership terms and community guidelines.
                </span>
              </label>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t">
            {step > 1 ? (
              <Button
                data-testid="signup-back-btn"
                type="button"
                variant="outline"
                onClick={prevStep}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
            ) : (
              <Button
                data-testid="signup-cancel-btn"
                type="button"
                variant="ghost"
                onClick={onClose}
                className="text-gray-500"
              >
                Cancel
              </Button>
            )}

            {step < 4 ? (
              <Button
                data-testid="signup-continue-btn"
                type="button"
                onClick={nextStep}
                className="btn-bundle flex items-center gap-2"
              >
                Continue
                <ArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                data-testid="signup-submit-btn"
                type="button"
                onClick={handleSubmit}
                disabled={loading || !formData.agree_terms || !formData.invite_code}
                className="btn-bundle flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  <>
                    Join The Bundle
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
